"use client";

import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cn } from "../lib/cn";
import { useReducedMotion } from "../hooks/useReducedMotion";

/**
 * BorderGlow — steady halo around the perimeter of a surface, optionally
 * breathing.
 *
 * Adapted from React Bits Pro `@reactbits-starter/border-glow-tw`,
 * re-implemented against pretty's anatomy. Sibling to the existing
 * `<BorderBeam>` (used internally on `<Badge variant="premium">`):
 *
 * - **BorderBeam** — sweeping bright arc rotating around the border
 *   (an "earned attention" treatment).
 * - **BorderGlow** — steady or pulsing halo around the perimeter (an
 *   "active / special / stay-here" treatment).
 *
 * The two compose: `<BorderBeam>` wraps `<BorderGlow>` (or vice versa) on
 * the same host renders a "premium + active" state cleanly.
 *
 * Glow color drives from theme tokens (`--primary` / `--accent` /
 * `--success` / `--warning`) — never a hardcoded hex. Pulse animation
 * reuses pretty's preset `animate-pulse-glow` keyframe by overriding
 * `--shadow-glow` + `--shadow-glow-aurora` per-instance, so the same
 * keyframe drives any color without per-instance keyframe duplication.
 *
 * SSR-safe — `useReducedMotion()` resolves on the client and collapses
 * the pulse to a static glow (the affordance stays visible). For
 * `pulse={false}`, the static glow renders without animation regardless.
 *
 *     <BorderGlow color="primary" intensity="default">
 *       <StudioCard />
 *     </BorderGlow>
 *
 *     <BorderGlow color="success" pulse={false} asChild>
 *       <Link to="/upgrade">Upgrade</Link>
 *     </BorderGlow>
 */

const COLOR_TOKEN: Record<NonNullable<BorderGlowProps["color"]>, string> = {
  primary: "--primary",
  accent: "--accent",
  success: "--success",
  warning: "--warning",
};

interface IntensityStep {
  /** Spread radius — how far the glow reaches. */
  spread: number;
  /** Blur radius — how soft the glow edge is. */
  blur: number;
  /** Alpha at the dim end of the pulse (also used as the static value). */
  alphaDim: number;
  /** Alpha at the bright end of the pulse. */
  alphaBright: number;
}

const INTENSITY: Record<NonNullable<BorderGlowProps["intensity"]>, IntensityStep> = {
  subtle: { spread: 8, blur: 12, alphaDim: 0.18, alphaBright: 0.32 },
  default: { spread: 16, blur: 24, alphaDim: 0.32, alphaBright: 0.5 },
  strong: { spread: 24, blur: 40, alphaDim: 0.45, alphaBright: 0.7 },
};

export interface BorderGlowProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Glow strength. `subtle` for grids, `strong` for hero surfaces. Default `default`. */
  intensity?: "subtle" | "default" | "strong";
  /** Theme token that drives the halo color. Default `accent` (matches BorderBeam). */
  color?: "primary" | "accent" | "success" | "warning";
  /**
   * Pulse the glow between dim and bright. Default `true`. `useReducedMotion()`
   * auto-disables the pulse — the affordance stays visible as a static glow.
   * Set `pulse={false}` to ship a static glow regardless of the user's motion
   * preference.
   */
  pulse?: boolean;
  /**
   * How the glow is anchored:
   * - `"none"` (default) — the steady / pulsing perimeter halo (box-shadow).
   * - `"cursor"` — a masked-radial ring that **follows the pointer** on hover.
   *   Renders a wrapping `relative` div with an absolutely-positioned, masked
   *   `[data-glow]` ring sibling; JS writes only `--mx`/`--my` on `pointermove`,
   *   CSS paints. `pointer-events:none`, so the host stays fully interactive.
   *   Decorative and hover-only: under reduced motion the ring is not rendered
   *   (the host is fully usable without it). `intensity` / `pulse` do not apply;
   *   `color` still selects the token. `asChild` is ignored in this mode (the
   *   ring must be a real sibling, which Slot cannot inject). Borrowed structure:
   *   Frontend Masters "Glowing Hover Effect that Follows the Pointer".
   */
  track?: "none" | "cursor";
  /** Forward props onto a single slotted child instead of rendering a wrapping div. */
  asChild?: boolean;
}

export const BorderGlow = React.forwardRef<HTMLDivElement, BorderGlowProps>(
  (
    {
      className,
      intensity = "default",
      color = "accent",
      pulse = true,
      track = "none",
      asChild = false,
      style,
      children,
      ...props
    },
    ref,
  ) => {
    const reduced = useReducedMotion();
    const animatePulse = pulse && reduced !== true;

    const step = INTENSITY[intensity];
    const token = COLOR_TOKEN[color];

    // Cursor-tracked glow: a masked radial ring that follows the pointer. Only
    // --mx/--my are written from JS (no re-render); CSS does the paint. The ring
    // is decorative + hover-only, so under reduced motion it is simply not
    // rendered and no pointer handlers are attached (the host stays usable).
    const glowRef = React.useRef<HTMLSpanElement>(null);
    if (track === "cursor") {
      const handleMove = (e: React.MouseEvent<HTMLDivElement>) => {
        const el = glowRef.current;
        if (!el) return;
        const r = e.currentTarget.getBoundingClientRect();
        el.style.setProperty("--mx", `${e.clientX - r.left}px`);
        el.style.setProperty("--my", `${e.clientY - r.top}px`);
        el.style.opacity = "1";
      };
      const handleLeave = () => {
        if (glowRef.current) glowRef.current.style.opacity = "0";
      };
      return (
        <div
          ref={ref}
          className={cn("relative", className)}
          onMouseMove={reduced ? undefined : handleMove}
          onMouseLeave={reduced ? undefined : handleLeave}
          style={style}
          {...props}
        >
          {reduced ? null : (
            <span
              ref={glowRef}
              aria-hidden
              data-glow
              className="pointer-events-none absolute inset-0 z-10 rounded-[inherit] p-[1.4px] opacity-0 transition-opacity duration-300"
              style={{
                background: `radial-gradient(130px circle at var(--mx, 50%) var(--my, 50%), hsl(var(${token})), transparent 62%)`,
                WebkitMask: "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
                WebkitMaskComposite: "xor",
                maskComposite: "exclude",
                maskRepeat: "no-repeat",
              }}
            />
          )}
          {children}
        </div>
      );
    }

    const dimShadow = `0 0 ${step.blur}px ${step.spread}px hsl(var(${token}) / ${step.alphaDim})`;
    const brightShadow = `0 0 ${Math.round(step.blur * 1.4)}px ${Math.round(step.spread * 1.3)}px hsl(var(${token}) / ${step.alphaBright})`;

    const Comp = asChild ? Slot : "div";

    // Per-instance overrides for the preset keyframe. The preset's
    // `pulse-glow` animation references `var(--shadow-glow)` and
    // `var(--shadow-glow-aurora)`; setting them inline lets one keyframe
    // drive any color/intensity combination without a per-instance
    // <style> tag.
    const mergedStyle: React.CSSProperties = {
      ...style,
      ["--shadow-glow" as string]: dimShadow,
      ["--shadow-glow-aurora" as string]: brightShadow,
    };

    return (
      <Comp
        ref={ref}
        className={cn(
          "relative",
          animatePulse
            ? "animate-pulse-glow"
            : // Static glow — no animation, just the dim box-shadow.
              "shadow-[var(--shadow-glow)]",
          className,
        )}
        style={mergedStyle}
        {...props}
      >
        {children}
      </Comp>
    );
  },
);
BorderGlow.displayName = "BorderGlow";
